/**
 * OnlineAssessmentsPanel — Phase 2B trainer surface for scheduled, timed,
 * question-bank-driven online assessments taken by agents in their portal.
 */
import { useEffect, useState, useCallback } from 'react';
import {
  Plus, Trash2, CalendarClock, Users, Trophy, Mail, Pencil, BellRing, UserPlus, RotateCcw, Eye, Download, AlertTriangle,
  ChevronDown, ChevronRight, Search, RefreshCcw,
} from 'lucide-react';
import { ReconcileResultsDialog } from './ReconcileResultsDialog';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import FreeTextReviewTab from './FreeTextReviewTab';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { BankUnpublishedHint } from '../../components/banks/BankUnpublishedHint';
import { useTraineeFilter, ALL } from './TraineeFilterContext';
import apiClient from '../../api/client';

const WINDOW_BADGE = {
  upcoming: { label: 'Upcoming', cls: 'bg-slate-100 text-slate-700' },
  open: { label: 'Open', cls: 'bg-emerald-100 text-emerald-800' },
  closed: { label: 'Closed', cls: 'bg-rose-100 text-rose-800' },
};

export default function OnlineAssessmentsPanel() {
  const { get, del } = useApi();
  const { hasRole } = useAuth();
  const canWrite = hasRole('trainer');
  const { departmentId, batchId, batches } = useTraineeFilter();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // filter bar + pagination (25/page) + collapsed "Closed" group
  const PAGE = 25;
  const [filters, setFilters] = useState({ batch: '', status: '', q: '', from: '', to: '' });
  const [qDraft, setQDraft] = useState('');
  const [page, setPage] = useState(0);
  const [meta, setMeta] = useState({ total: 0, counts: {} });
  const [showClosed, setShowClosed] = useState(false);
  const [showReconcile, setShowReconcile] = useState(false);
  const scopedBatchId = filters.batch || (batchId !== ALL ? batchId : '');
  const scopedBatch = (batches || []).find(b => b.batch_id === scopedBatchId);
  useEffect(() => { const h = setTimeout(() => setFilters(f => ({ ...f, q: qDraft })), 300); return () => clearTimeout(h); }, [qDraft]);
  useEffect(() => { setPage(0); }, [filters, batchId, departmentId]);
  const [openCreate, setOpenCreate] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [remindRow, setRemindRow] = useState(null);
  const [assignRow, setAssignRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      const effBatch = filters.batch || (batchId !== ALL ? batchId : '');
      if (effBatch) p.set('batch_id', effBatch);
      else if (departmentId !== ALL) p.set('department_id', departmentId);
      if (filters.status) p.set('status', filters.status);
      if (filters.q) p.set('q', filters.q);
      if (filters.from) p.set('date_from', filters.from);
      if (filters.to) p.set('date_to', filters.to);
      p.set('skip', String(page * PAGE)); p.set('limit', String(PAGE));
      const res = await get(`/online-assessments?${p}`);
      setRows(res.assessments || []);
      setMeta({ total: res.total ?? (res.assessments || []).length, counts: res.counts || {} });
    } catch { toast.error('Failed to load online assessments'); }
    finally { setLoading(false); }
  }, [get, batchId, departmentId, filters, page]);

  useEffect(() => { load(); }, [load]);

  const renderRows = (list) => list.map(a => {
              const badge = WINDOW_BADGE[a.window] || WINDOW_BADGE.upcoming;
              return (
                <Card key={a.assessment_id} className="p-4" data-testid={`online-assessment-${a.assessment_id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <button className="text-left min-w-0" onClick={() => setDetailId(a.assessment_id)}
                            data-testid={`open-online-assessment-${a.assessment_id}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display font-semibold">{a.name}</h3>
                        <Badge className={`${badge.cls} border-0`}>{badge.label}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(a.start_at).toLocaleString()} → {new Date(a.end_at).toLocaleString()}
                        {' '}· {a.duration_minutes} min · Pass {a.passing_pct}% · {a.question_count} Qs
                        {' '}· Bank: {a.question_bank_title}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        <Users className="w-3 h-3 inline mr-1" />
                        {a.submitted_count}/{a.target_count} submitted
                        {a.avg_score != null && <> · Avg {a.avg_score}%</>}
                        {a.invites_sent > 0 && <> · <Mail className="w-3 h-3 inline" /> {a.invites_sent} invited</>}
                      </p>
                    </button>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" title="View results & responses"
                              onClick={() => setDetailId(a.assessment_id)}
                              data-testid={`view-results-online-assessment-${a.assessment_id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      {canWrite && (
                        <>
                        <Button size="sm" variant="ghost" title="Assign to individual trainees"
                                onClick={() => setAssignRow(a)}
                                data-testid={`assign-online-assessment-${a.assessment_id}`}>
                          <UserPlus className="w-4 h-4" />
                        </Button>
                        {a.window !== 'closed' && (
                          <Button size="sm" variant="ghost" title="Send reminder"
                                  onClick={() => setRemindRow(a)}
                                  data-testid={`remind-online-assessment-${a.assessment_id}`}>
                            <BellRing className="w-4 h-4" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" title="Edit schedule"
                                onClick={() => setEditRow(a)}
                                data-testid={`edit-online-assessment-${a.assessment_id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" title="Delete"
                                onClick={async () => {
                                  try {
                                    await del(`/online-assessments/${a.assessment_id}`);
                                    toast.success('Assessment deleted');
                                    load();
                                  } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
                                }}
                                data-testid={`delete-online-assessment-${a.assessment_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              );
  });

  return (
    <div className="space-y-4" data-testid="online-assessments-panel">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          Scheduled, timed assessments taken by agents — questions shuffle per trainee.
        </p>
        {canWrite && (
          <div className="flex items-center gap-2">
            {scopedBatchId && (
              <Button variant="outline" onClick={() => setShowReconcile(true)} data-testid="oa-reconcile-btn"
                      title="Find submitted assessments not credited to anyone on this batch's roster and restore them">
                <RefreshCcw className="w-4 h-4 mr-1" /> Reconcile results
              </Button>
            )}
            <Button onClick={() => setOpenCreate(true)} data-testid="create-online-assessment-btn">
              <Plus className="w-4 h-4 mr-1" /> Create Assessment
            </Button>
          </div>
        )}
      </div>
      {scopedBatchId && (
        <ReconcileResultsDialog open={showReconcile} onOpenChange={setShowReconcile}
                                scope={{ batchId: scopedBatchId, batchName: scopedBatch?.name }} onDone={load} />
      )}

      <div className="flex flex-wrap items-end gap-2 text-sm" data-testid="oa-filter-bar">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground" />
          <Input value={qDraft} onChange={e => setQDraft(e.target.value)} placeholder="Search name or bank…"
                 className="pl-7 h-8 w-56" data-testid="oa-filter-search" />
        </div>
        <Select value={filters.batch || '__all__'} onValueChange={v => setFilters({ ...filters, batch: v === '__all__' ? '' : v })}>
          <SelectTrigger className="h-8 w-56" data-testid="oa-filter-batch"><SelectValue placeholder="Batch" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">{batchId !== ALL ? 'Selected batch (from filter)' : 'All batches'}</SelectItem>
            {(batches || []).map(b => <SelectItem key={b.batch_id} value={b.batch_id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.status || '__all__'} onValueChange={v => setFilters({ ...filters, status: v === '__all__' ? '' : v })}>
          <SelectTrigger className="h-8 w-40" data-testid="oa-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Any status</SelectItem>
            <SelectItem value="upcoming">Scheduled</SelectItem>
            <SelectItem value="open">Live</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <Input type="date" value={filters.from} onChange={e => setFilters({ ...filters, from: e.target.value })}
               className="h-8 w-40" data-testid="oa-filter-from" title="Starts on/after" />
        <Input type="date" value={filters.to} onChange={e => setFilters({ ...filters, to: e.target.value })}
               className="h-8 w-40" data-testid="oa-filter-to" title="Starts on/before" />
        {(filters.batch || filters.status || filters.q || filters.from || filters.to) && (
          <Button size="sm" variant="ghost" onClick={() => { setQDraft(''); setFilters({ batch: '', status: '', q: '', from: '', to: '' }); }}
                  data-testid="oa-filter-clear">Clear</Button>
        )}
        <span className="ml-auto text-xs text-muted-foreground" data-testid="oa-total">
          {meta.total} assessment{meta.total === 1 ? '' : 's'}
          {meta.counts?.closed != null && <> · {meta.counts.closed} closed</>}
        </span>
      </div>

      {loading ? <p className="text-sm text-muted-foreground">Loading…</p>
        : rows.length === 0 ? (
          <Card className="p-10 text-center">
            <CalendarClock className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
            <p className="font-display font-semibold">No online assessments yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Create one to schedule a timed, question-bank-driven test with automatic scoring and email invites.
            </p>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="online-assessment-list">
            {renderRows(rows.filter(a => a.window !== 'closed'))}
            {rows.some(a => a.window === 'closed') && (
              <div data-testid="oa-closed-group">
                <button type="button" onClick={() => setShowClosed(v => !v)}
                        className="flex items-center gap-2 text-sm text-muted-foreground py-2"
                        data-testid="oa-closed-toggle">
                  {showClosed ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  Closed
                  <Badge variant="outline" className="text-[10px]">{rows.filter(a => a.window === 'closed').length} on this page</Badge>
                </button>
                {showClosed && <div className="space-y-3">{renderRows(rows.filter(a => a.window === 'closed'))}</div>}
              </div>
            )}
            {meta.total > PAGE && (
              <div className="flex items-center justify-between text-xs text-muted-foreground pt-2" data-testid="oa-pagination">
                <span>Page {page + 1} of {Math.ceil(meta.total / PAGE)}</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="oa-prev">Previous</Button>
                  <Button size="sm" variant="outline" disabled={(page + 1) * PAGE >= meta.total} onClick={() => setPage(p => p + 1)} data-testid="oa-next">Next</Button>
                </div>
              </div>
            )}
          </div>
        )}

      {openCreate && <CreateDialog onClose={(saved) => { setOpenCreate(false); if (saved) load(); }} />}
      {detailId && <DetailDialog assessmentId={detailId} canWrite={canWrite} onClose={() => setDetailId(null)} />}
      {editRow && <EditScheduleDialog row={editRow} onClose={(saved) => { setEditRow(null); if (saved) load(); }} />}
      {remindRow && <RemindDialog row={remindRow} onClose={() => setRemindRow(null)} />}
      {assignRow && <AssignDialog assessment={assignRow} onClose={(saved) => { setAssignRow(null); if (saved) load(); }} />}
    </div>
  );
}

function CreateDialog({ onClose }) {
  const { get, post } = useApi();
  const { departments, batches } = useTraineeFilter();
  const NONE = '__none__';

  const [form, setForm] = useState({
    name: '', description: '', department_id: '', batch_ids: [],
    start_at: '', end_at: '', duration_minutes: 30, passing_pct: 50,
    attempts_allowed: 1, question_bank_id: '', question_count: '',
    send_invites: true,
  });
  const [banks, setBanks] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    get('/question-banks?limit=100')
      .then(r => setBanks(r.banks || r.question_banks || []))
      .catch(() => setBanks([]));
  }, [get]);

  const deptBatches = form.department_id
    ? batches.filter(b => b.department_id === form.department_id)
    : batches;

  const onSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (form.batch_ids.length === 0) { toast.error('Select at least one batch'); return; }
    if (!form.start_at || !form.end_at) { toast.error('Set the schedule window'); return; }
    if (!form.question_bank_id) { toast.error('Select a question bank'); return; }
    setSaving(true);
    try {
      await post('/online-assessments', {
        name: form.name.trim(),
        description: form.description || null,
        department_id: form.department_id || null,
        batch_ids: form.batch_ids,
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
        duration_minutes: Number(form.duration_minutes),
        passing_pct: Number(form.passing_pct),
        attempts_allowed: Number(form.attempts_allowed),
        question_bank_id: form.question_bank_id,
        question_count: form.question_count ? Number(form.question_count) : null,
        send_invites: form.send_invites,
      });
      toast.success(form.send_invites
        ? 'Assessment published — email invites are being sent'
        : 'Assessment published');
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Publish failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="create-online-assessment-dialog">
        <DialogHeader>
          <DialogTitle>Create Assessment</DialogTitle>
          <DialogDescription>Schedule a timed, auto-graded test for a batch.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Department</Label>
              <Select value={form.department_id || NONE}
                      onValueChange={v => setForm(f => ({ ...f, department_id: v === NONE ? '' : v, batch_ids: [] }))}>
                <SelectTrigger data-testid="oa-department"><SelectValue placeholder="All departments" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>All departments</SelectItem>
                  {departments.map(d => <SelectItem key={d.department_id} value={d.department_id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Batches * <span className="text-xs text-muted-foreground">({form.batch_ids.length} selected)</span></Label>
              <div className="border rounded-md max-h-32 overflow-y-auto divide-y" data-testid="oa-batches">
                {deptBatches.map(b => (
                  <label key={b.batch_id}
                         className={`flex items-center gap-2 px-2.5 py-1.5 text-sm cursor-pointer hover:bg-muted/50 ${form.batch_ids.includes(b.batch_id) ? 'bg-primary/5' : ''}`}
                         data-testid={`oa-batch-option-${b.batch_id}`}>
                    <input type="checkbox" checked={form.batch_ids.includes(b.batch_id)}
                           onChange={() => setForm(f => ({
                             ...f,
                             batch_ids: f.batch_ids.includes(b.batch_id)
                               ? f.batch_ids.filter(x => x !== b.batch_id)
                               : [...f.batch_ids, b.batch_id],
                           }))} />
                    {b.name}
                  </label>
                ))}
              </div>
              {form.batch_ids.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1" data-testid="oa-selected-batches">
                  Assigning to: {form.batch_ids.map(id => batches.find(b => b.batch_id === id)?.name).filter(Boolean).join(', ')}
                </p>
              )}
            </div>
          </div>
          <div>
            <Label>Assessment name *</Label>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                   placeholder="e.g. Outbound Sales Day 3" data-testid="oa-name" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={2} value={form.description}
                      onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start time *</Label>
              <Input type="datetime-local" value={form.start_at}
                     onChange={e => setForm(f => ({ ...f, start_at: e.target.value }))}
                     data-testid="oa-start" />
            </div>
            <div>
              <Label>End time *</Label>
              <Input type="datetime-local" value={form.end_at}
                     onChange={e => setForm(f => ({ ...f, end_at: e.target.value }))}
                     data-testid="oa-end" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Duration (min)</Label>
              <Input type="number" min={1} value={form.duration_minutes}
                     onChange={e => setForm(f => ({ ...f, duration_minutes: e.target.value }))}
                     data-testid="oa-duration" />
            </div>
            <div>
              <Label>Passing %</Label>
              <Input type="number" min={0} max={100} value={form.passing_pct}
                     onChange={e => setForm(f => ({ ...f, passing_pct: e.target.value }))}
                     data-testid="oa-passing" />
            </div>
            <div>
              <Label>Attempts</Label>
              <Input type="number" min={1} max={10} value={form.attempts_allowed}
                     onChange={e => setForm(f => ({ ...f, attempts_allowed: e.target.value }))}
                     data-testid="oa-attempts" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Question bank *</Label>
              <Select value={form.question_bank_id || undefined}
                      onValueChange={v => setForm(f => ({ ...f, question_bank_id: v }))}>
                <SelectTrigger data-testid="oa-bank"><SelectValue placeholder="Select bank" /></SelectTrigger>
                <SelectContent>
                  {banks.map(b => (
                    <SelectItem key={b.bank_id} value={b.bank_id}>
                      {b.title} ({b.item_count ?? '?'} Qs{b.unpublished_item_count > 0 ? ` · ${b.unpublished_item_count} unpublished` : ''})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(() => {
                const picked = banks.find(b => b.bank_id === form.question_bank_id);
                return picked?.lifecycle_status === 'published' ? (
                  <div className="mt-1.5">
                    <BankUnpublishedHint bank={picked}
                      onPublished={(u) => setBanks(prev => prev.map(b => b.bank_id === u.bank_id ? { ...b, ...u } : b))} />
                  </div>
                ) : null;
              })()}
            </div>
            <div>
              <Label># Questions (blank = all)</Label>
              <Input type="number" min={1} value={form.question_count}
                     onChange={e => setForm(f => ({ ...f, question_count: e.target.value }))}
                     data-testid="oa-question-count" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={form.send_invites}
                      onCheckedChange={c => setForm(f => ({ ...f, send_invites: !!c }))}
                      data-testid="oa-send-invites" />
            Email an invitation to every targeted trainee
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={saving} data-testid="oa-publish-btn">
            {saving ? 'Publishing…' : 'Publish'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailDialog({ assessmentId, canWrite, onClose }) {
  const { get } = useApi();
  const [data, setData] = useState(null);
  const [reassign, setReassign] = useState(null);
  const [responsesFor, setResponsesFor] = useState(null);

  const exportResponses = async () => {
    try {
      const res = await apiClient.get(`/online-assessments/${assessmentId}/responses.csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(data?.name || 'assessment').replace(/[^\w\- ]/g, '')}-responses.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Responses exported');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    }
  };

  const loadDetail = useCallback(() => {
    get(`/online-assessments/${assessmentId}`).then(setData)
      .catch(() => toast.error('Failed to load detail'));
  }, [get, assessmentId]);

  useEffect(loadDetail, [loadDetail]);

  const an = data?.analytics;
  const pendingTotal = (data?.results || []).filter(r => r.pending_review).length;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="online-assessment-detail">
        <DialogHeader>
          <DialogTitle>{data?.name || 'Assessment'}</DialogTitle>
          <DialogDescription>Results, analytics and leaderboard.</DialogDescription>
        </DialogHeader>
        {!data ? <p className="text-sm text-muted-foreground">Loading…</p> : (
          <Tabs defaultValue="results">
            <TabsList>
              <TabsTrigger value="results" data-testid="oa-tab-results">Results</TabsTrigger>
              <TabsTrigger value="analytics" data-testid="oa-tab-analytics">Analytics</TabsTrigger>
              <TabsTrigger value="gaps" data-testid="oa-tab-gaps">Gap Analysis</TabsTrigger>
              <TabsTrigger value="leaderboard" data-testid="oa-tab-leaderboard">Leaderboard</TabsTrigger>
              <TabsTrigger value="review" data-testid="oa-tab-review">
                Review{pendingTotal > 0 ? ` (${pendingTotal})` : ''}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="results" className="mt-3">
              <div className="flex justify-end mb-2">
                <Button size="sm" variant="outline" onClick={exportResponses}
                        data-testid="oa-export-responses-btn">
                  <Download className="w-4 h-4 mr-1" /> Export Responses
                </Button>
              </div>
              <Table data-testid="oa-results-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Trainee</TableHead>
                    <TableHead className="w-20">Score</TableHead>
                    <TableHead className="w-20">Result</TableHead>
                    <TableHead className="w-20">Time</TableHead>
                    <TableHead className="w-24">Attempts</TableHead>
                    <TableHead className="w-36">Submitted</TableHead>
                    {canWrite && <TableHead className="w-28">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data.results || []).map(r => (
                    <TableRow key={r.trainee_id}>
                      <TableCell className="font-medium">{r.name}
                        {r.override && (
                          <Badge className="ml-1.5 bg-sky-100 text-sky-800 border-0 text-[10px]"
                                 title={`Assigned by ${r.override.granted_by_name || '—'} on ${new Date(r.override.granted_at).toLocaleString()}${r.override.reason ? ` — ${r.override.reason}` : ''}`}
                                 data-testid={`override-badge-${r.trainee_id}`}>
                            Individual{r.override.extra_attempts ? ` +${r.override.extra_attempts}` : ''}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{r.score_pct != null ? `${r.score_pct}%` : '—'}
                        {r.pending_review && (
                          <Badge className="ml-1.5 bg-amber-100 text-amber-800 border-0 text-[10px]"
                                 data-testid={`pending-review-${r.trainee_id}`}>Review pending</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {r.passed == null ? <span className="text-xs text-muted-foreground">—</span>
                          : r.passed ? <Badge className="bg-emerald-100 text-emerald-800 border-0">Pass</Badge>
                            : <Badge className="bg-rose-100 text-rose-800 border-0">Fail</Badge>}
                      </TableCell>
                      <TableCell className="text-xs">{r.time_taken_seconds ? `${Math.round(r.time_taken_seconds / 60)}m` : '—'}</TableCell>
                      <TableCell className="text-xs">{r.attempts_used}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {r.submitted_at ? new Date(r.submitted_at).toLocaleString() : 'Not attempted'}
                      </TableCell>
                      {canWrite && (
                        <TableCell>
                          <div className="flex items-center">
                            <Button size="sm" variant="ghost" title="View responses"
                                    onClick={() => setResponsesFor(r)}
                                    disabled={!r.submitted_at}
                                    data-testid={`responses-${r.trainee_id}`}>
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" title="Reassign to this trainee"
                                    onClick={() => setReassign(r)}
                                    data-testid={`reassign-${r.trainee_id}`}>
                              <RotateCcw className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabsContent>

            <TabsContent value="analytics" className="mt-3 space-y-4">
              {an && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="oa-analytics-cards">
                  <MiniStat label="Average" value={an.average != null ? `${an.average}%` : '—'} />
                  <MiniStat label="Highest" value={an.highest != null ? `${an.highest}%` : '—'} />
                  <MiniStat label="Lowest" value={an.lowest != null ? `${an.lowest}%` : '—'} />
                  <MiniStat label="Avg Time" value={an.avg_time_seconds ? `${Math.round(an.avg_time_seconds / 60)}m` : '—'} />
                  <MiniStat label="Pass %" value={an.pass_pct != null ? `${an.pass_pct}%` : '—'} />
                  <MiniStat label="Fail %" value={an.fail_pct != null ? `${an.fail_pct}%` : '—'} />
                  <MiniStat label="Submitted" value={an.submitted} />
                  <MiniStat label="Not Attempted" value={an.not_attempted} />
                </div>
              )}
              <div>
                <h4 className="font-display font-semibold text-sm mb-2">Question difficulty</h4>
                <Table data-testid="oa-difficulty-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Question</TableHead>
                      <TableHead className="w-28">Correct rate</TableHead>
                      <TableHead className="w-20">Skipped</TableHead>
                      <TableHead className="w-24">Difficulty</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.question_difficulty || []).map(q => (
                      <TableRow key={q.question_id}>
                        <TableCell className="text-sm">{q.question_text}</TableCell>
                        <TableCell>{q.correct_rate != null ? `${q.correct_rate}%` : '—'}</TableCell>
                        <TableCell>{q.skipped}</TableCell>
                        <TableCell>
                          {q.difficulty === 'hard' && <Badge className="bg-rose-100 text-rose-800 border-0">Hard</Badge>}
                          {q.difficulty === 'medium' && <Badge className="bg-amber-100 text-amber-800 border-0">Medium</Badge>}
                          {q.difficulty === 'easy' && <Badge className="bg-emerald-100 text-emerald-800 border-0">Easy</Badge>}
                          {!q.difficulty && <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="gaps" className="mt-3">
              <GapAnalysisTab gap={data.gap_analysis} />
            </TabsContent>


            <TabsContent value="leaderboard" className="mt-3">
              <Table data-testid="oa-leaderboard-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Rank</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="w-24">Score</TableHead>
                    <TableHead className="w-24">Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data.leaderboard || []).map(r => (
                    <TableRow key={r.trainee_id}>
                      <TableCell>
                        {r.rank <= 3 ? <Trophy className={`w-4 h-4 ${r.rank === 1 ? 'text-amber-500' : r.rank === 2 ? 'text-slate-400' : 'text-amber-700'}`} /> : r.rank}
                      </TableCell>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell>{r.score_pct}%</TableCell>
                      <TableCell className="text-xs">{r.time_taken_seconds ? `${Math.round(r.time_taken_seconds / 60)}m ${r.time_taken_seconds % 60}s` : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabsContent>

            <TabsContent value="review" className="mt-3">
              <FreeTextReviewTab assessmentId={assessmentId} onGraded={loadDetail} />
            </TabsContent>
          </Tabs>
        )}
        {reassign && data && (
          <AssignDialog assessment={data} prefill={reassign}
                        onClose={(saved) => { setReassign(null); if (saved) loadDetail(); }} />
        )}
        {responsesFor && (
          <ResponsesDialog assessmentId={assessmentId} trainee={responsesFor}
                           onClose={() => setResponsesFor(null)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function MiniStat({ label, value }) {
  return (
    <Card className="p-3">
      <p className="text-lg font-bold font-display leading-none">{value}</p>
      <p className="text-[11px] text-muted-foreground mt-1">{label}</p>
    </Card>
  );
}

// datetime-local needs "YYYY-MM-DDTHH:mm" in the browser's local timezone
function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function EditScheduleDialog({ row, onClose }) {
  const { put } = useApi();
  const [form, setForm] = useState({
    start_at: toLocalInput(row.start_at),
    end_at: toLocalInput(row.end_at),
    duration_minutes: row.duration_minutes,
    attempts_allowed: row.attempts_allowed,
  });
  const [saving, setSaving] = useState(false);

  const onSave = async () => {
    if (!form.start_at || !form.end_at) { toast.error('Both start and end times are required'); return; }
    if (new Date(form.end_at) <= new Date(form.start_at)) { toast.error('End time must be after start time'); return; }
    setSaving(true);
    try {
      const res = await put(`/online-assessments/${row.assessment_id}`, {
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
        duration_minutes: Number(form.duration_minutes),
        attempts_allowed: Number(form.attempts_allowed),
      });
      toast.success(res.schedule_updated
        ? 'Schedule updated — trainees are being notified by email'
        : 'Assessment updated');
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-w-md" data-testid="edit-schedule-dialog">
        <DialogHeader>
          <DialogTitle>Edit Schedule — {row.name}</DialogTitle>
          <DialogDescription>
            Changes apply to all invited trainees immediately. Extending the end time re-opens a closed
            assessment. Trainees receive a "schedule updated" email automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start time</Label>
              <Input type="datetime-local" value={form.start_at}
                     onChange={e => setForm(f => ({ ...f, start_at: e.target.value }))}
                     data-testid="edit-oa-start" />
            </div>
            <div>
              <Label>End time (deadline)</Label>
              <Input type="datetime-local" value={form.end_at}
                     onChange={e => setForm(f => ({ ...f, end_at: e.target.value }))}
                     data-testid="edit-oa-end" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Duration (min)</Label>
              <Input type="number" min={1} value={form.duration_minutes}
                     onChange={e => setForm(f => ({ ...f, duration_minutes: e.target.value }))}
                     data-testid="edit-oa-duration" />
            </div>
            <div>
              <Label>Attempts</Label>
              <Input type="number" min={1} max={10} value={form.attempts_allowed}
                     onChange={e => setForm(f => ({ ...f, attempts_allowed: e.target.value }))}
                     data-testid="edit-oa-attempts" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={saving} data-testid="edit-oa-save-btn">
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const REMIND_AUDIENCES = [
  { value: 'not_attempted', label: 'Not yet attempted', hint: 'Trainees with no attempt at all' },
  { value: 'in_progress', label: 'Started but not submitted', hint: 'Trainees mid-attempt without a submission' },
  { value: 'all', label: 'All invited trainees', hint: 'Everyone targeted by this assessment' },
];

function RemindDialog({ row, onClose }) {
  const { post } = useApi();
  const [audience, setAudience] = useState('not_attempted');
  const [sending, setSending] = useState(false);

  const onSend = async () => {
    setSending(true);
    try {
      const res = await post(`/online-assessments/${row.assessment_id}/remind`, { audience });
      toast.success(res.recipients > 0
        ? `Reminder queued for ${res.recipients} trainee${res.recipients === 1 ? '' : 's'}`
        : 'No trainees match that filter — nothing to send');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reminder');
    } finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="remind-dialog">
        <DialogHeader>
          <DialogTitle>Send Reminder — {row.name}</DialogTitle>
          <DialogDescription>
            Emails the assessment name, deadline, duration and a direct link.
            Limited to one reminder every 10 minutes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {REMIND_AUDIENCES.map(opt => (
            <label key={opt.value}
                   className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-colors ${audience === opt.value ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'}`}
                   data-testid={`remind-audience-${opt.value}`}>
              <input type="radio" name="remind-audience" className="mt-1"
                     checked={audience === opt.value}
                     onChange={() => setAudience(opt.value)} />
              <span>
                <span className="block text-sm font-medium">{opt.label}</span>
                <span className="block text-xs text-muted-foreground">{opt.hint}</span>
              </span>
            </label>
          ))}
          <p className="text-xs text-muted-foreground">
            {row.submitted_count}/{row.target_count} submitted so far.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose()}>Cancel</Button>
          <Button onClick={onSend} disabled={sending} data-testid="remind-send-btn">
            <BellRing className="w-4 h-4 mr-1" />
            {sending ? 'Sending…' : 'Send Reminder'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignDialog({ assessment, prefill, onClose }) {
  const { get, post } = useApi();
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(prefill ? [{ trainee_id: prefill.trainee_id, name: prefill.name, employee_id: prefill.employee_id }] : []);
  const [customWindow, setCustomWindow] = useState(false);
  const [form, setForm] = useState({
    start_at: toLocalInput(assessment.start_at),
    end_at: toLocalInput(assessment.end_at),
    duration_minutes: assessment.duration_minutes,
    extra_attempts: prefill ? 1 : 0,
    reason: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (search.trim().length < 2) { setOptions([]); return; }
    const t = setTimeout(() => {
      get(`/trainees?search=${encodeURIComponent(search.trim())}&limit=10`)
        .then(res => setOptions(res.trainees || []))
        .catch(() => setOptions([]));
    }, 300);
    return () => clearTimeout(t);
  }, [search, get]);

  const toggle = (t) => {
    setSelected(sel => sel.some(x => x.trainee_id === t.trainee_id)
      ? sel.filter(x => x.trainee_id !== t.trainee_id)
      : [...sel, { trainee_id: t.trainee_id, name: t.name, employee_id: t.employee_id }]);
  };

  const onAssign = async () => {
    if (selected.length === 0) { toast.error('Select at least one trainee'); return; }
    if (customWindow && new Date(form.end_at) <= new Date(form.start_at)) {
      toast.error('End time must be after start time'); return;
    }
    setSaving(true);
    try {
      const res = await post(`/online-assessments/${assessment.assessment_id}/assign`, {
        trainee_ids: selected.map(s => s.trainee_id),
        start_at: customWindow ? new Date(form.start_at).toISOString() : null,
        end_at: customWindow ? new Date(form.end_at).toISOString() : null,
        duration_minutes: customWindow ? Number(form.duration_minutes) : null,
        extra_attempts: Number(form.extra_attempts) || 0,
        reason: form.reason || null,
      });
      toast.success(`Assigned to ${res.assigned} trainee${res.assigned === 1 ? '' : 's'} — invites are being emailed`);
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Assignment failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle>{prefill ? 'Reassign' : 'Assign to trainees'} — {assessment.name}</DialogTitle>
          <DialogDescription>
            {prefill
              ? 'Grant this trainee another attempt — e.g. after an accidental submission — optionally with a new window.'
              : 'Assign this assessment to individual trainees (absentees, other batches) with an optional separate window.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Search trainees (name / employee ID)</Label>
            <Input value={search} onChange={e => setSearch(e.target.value)}
                   placeholder="Type at least 2 characters…" data-testid="assign-search-input" />
            {options.length > 0 && (
              <div className="mt-1 border rounded-md divide-y max-h-44 overflow-y-auto" data-testid="assign-search-results">
                {options.map(t => {
                  const isSel = selected.some(x => x.trainee_id === t.trainee_id);
                  return (
                    <button key={t.trainee_id} type="button" onClick={() => toggle(t)}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-muted/50 ${isSel ? 'bg-primary/5' : ''}`}
                            data-testid={`assign-option-${t.trainee_id}`}>
                      <span>{t.name}
                        <span className="text-xs text-muted-foreground ml-2">{t.employee_id || t.email || ''}</span>
                      </span>
                      {isSel && <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px]">Selected</Badge>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="assign-selected-list">
              {selected.map(s => (
                <Badge key={s.trainee_id} variant="outline" className="cursor-pointer"
                       onClick={() => toggle(s)} title="Click to remove">
                  {s.name} ✕
                </Badge>
              ))}
            </div>
          )}
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={customWindow}
                   onChange={e => setCustomWindow(e.target.checked)}
                   data-testid="assign-custom-window-toggle" />
            Set a separate window for these trainees
          </label>
          {customWindow && (
            <div className="grid grid-cols-2 gap-3 pl-1">
              <div>
                <Label>Start time</Label>
                <Input type="datetime-local" value={form.start_at}
                       onChange={e => setForm(f => ({ ...f, start_at: e.target.value }))}
                       data-testid="assign-start-input" />
              </div>
              <div>
                <Label>End time</Label>
                <Input type="datetime-local" value={form.end_at}
                       onChange={e => setForm(f => ({ ...f, end_at: e.target.value }))}
                       data-testid="assign-end-input" />
              </div>
              <div>
                <Label>Duration (min)</Label>
                <Input type="number" min={1} value={form.duration_minutes}
                       onChange={e => setForm(f => ({ ...f, duration_minutes: e.target.value }))}
                       data-testid="assign-duration-input" />
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Extra attempts</Label>
              <Input type="number" min={0} max={10} value={form.extra_attempts}
                     onChange={e => setForm(f => ({ ...f, extra_attempts: e.target.value }))}
                     data-testid="assign-extra-attempts-input" />
              <p className="text-[11px] text-muted-foreground mt-1">
                Use 1+ to grant a retake after an accidental submission.
              </p>
            </div>
            <div>
              <Label>Reason (audit trail)</Label>
              <Input value={form.reason} placeholder="e.g. Absent on test day"
                     onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                     data-testid="assign-reason-input" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>Cancel</Button>
          <Button onClick={onAssign} disabled={saving} data-testid="assign-submit-btn">
            <UserPlus className="w-4 h-4 mr-1" />
            {saving ? 'Assigning…' : prefill ? 'Reassign' : 'Assign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResponsesDialog({ assessmentId, trainee, onClose }) {
  const { get } = useApi();
  const [data, setData] = useState(null);

  useEffect(() => {
    get(`/online-assessments/${assessmentId}/responses/${trainee.trainee_id}`)
      .then(setData)
      .catch(e => { toast.error(e.response?.data?.detail || 'Failed to load responses'); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId, trainee.trainee_id]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="responses-dialog">
        <DialogHeader>
          <DialogTitle>Responses — {trainee.name}</DialogTitle>
          <DialogDescription>
            Per-question answers for every submitted attempt. Use this to spot knowledge gaps for coaching.
          </DialogDescription>
        </DialogHeader>
        {!data && <p className="text-sm text-muted-foreground py-6 text-center">Loading responses…</p>}
        {data && data.attempts.length === 0 && (
          <p className="text-sm text-muted-foreground py-6 text-center">No submitted attempts yet.</p>
        )}
        <div className="space-y-5">
          {(data?.attempts || []).map(at => (
            <div key={at.attempt_no} data-testid={`attempt-${at.attempt_no}`}>
              <div className="flex items-center gap-2 mb-2">
                <p className="text-sm font-semibold">Attempt {at.attempt_no}</p>
                <Badge className={at.passed ? 'bg-emerald-100 text-emerald-800 border-0' : 'bg-red-100 text-red-800 border-0'}>
                  {at.score_pct != null ? `${at.score_pct}%` : '—'} · {at.pending_review ? 'Pending review' : at.passed ? 'Pass' : 'Fail'}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {at.submitted_at ? new Date(at.submitted_at).toLocaleString() : ''}
                  {at.time_taken_seconds != null ? ` · ${Math.round(at.time_taken_seconds / 60)}m ${at.time_taken_seconds % 60}s` : ''}
                </span>
              </div>
              <div className="border rounded-md divide-y">
                {at.responses.map((r, i) => (
                  <div key={i} className="px-3 py-2.5 text-sm">
                    <p className="font-medium mb-1">{i + 1}. {r.question_text}
                      <span className="text-xs text-muted-foreground ml-2">
                        {r.points_awarded != null ? `${r.points_awarded}/` : ''}{r.points} pt{r.points === 1 ? '' : 's'}
                      </span>
                    </p>
                    <p className={`text-xs ${r.pending_review ? 'text-amber-700' : r.correct ? 'text-emerald-700' : 'text-red-700'}`}>
                      {r.skipped ? 'Skipped' : <>Answer: {r.your_answer}</>}
                      {r.pending_review ? ' · awaiting manual review' : r.correct ? ' ✓' : ' ✗'}
                    </p>
                    {!r.correct && !r.pending_review && (
                      <p className="text-xs text-muted-foreground">Correct: {r.correct_answer}</p>
                    )}
                    {r.remarks && <p className="text-xs text-muted-foreground italic">Reviewer: {r.remarks}</p>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GapAnalysisTab({ gap }) {
  const [expanded, setExpanded] = useState(null);
  const rows = gap?.questions || [];
  if (!gap || gap.total_submitted === 0) {
    return <p className="text-sm text-muted-foreground py-6 text-center" data-testid="oa-gaps-empty">
      No submitted attempts yet — gap analysis appears once agents take the assessment.
    </p>;
  }
  const missColor = (pct) => pct >= 50 ? 'bg-rose-500' : pct >= 25 ? 'bg-amber-500' : 'bg-emerald-500';
  const priorities = rows.filter(r => r.miss_pct >= 40).length;
  return (
    <div className="space-y-3" data-testid="oa-gaps-tab">
      <div className="flex items-center gap-2 text-sm">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <span className="text-muted-foreground">
          Ranked by miss rate across {gap.total_submitted} submitted agent{gap.total_submitted === 1 ? '' : 's'}
          {priorities > 0 && <> · <span className="font-medium text-foreground">{priorities} retraining {priorities === 1 ? 'priority' : 'priorities'} (≥40% missed)</span></>}
        </span>
      </div>
      <div className="space-y-2">
        {rows.map((q, i) => (
          <div key={q.question_id}
               className={`border rounded-md p-3 ${q.miss_pct >= 40 ? 'border-rose-200 bg-rose-50/40' : ''}`}
               data-testid={`gap-row-${q.question_id}`}>
            <div className="flex items-start gap-3">
              <span className="text-xs font-mono text-muted-foreground mt-0.5 w-5 shrink-0">#{i + 1}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium leading-snug">{q.question_text}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <div className="h-1.5 rounded-full bg-muted flex-1 max-w-[220px] overflow-hidden">
                    <div className={`h-full rounded-full ${missColor(q.miss_pct)}`}
                         style={{ width: `${Math.min(q.miss_pct, 100)}%` }} />
                  </div>
                  <span className="text-xs font-semibold" data-testid={`gap-miss-pct-${q.question_id}`}>
                    {q.miss_pct}% missed
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {q.wrong} wrong · {q.skipped} skipped · {q.correct} correct · seen by {q.seen_by}
                  </span>
                </div>
                {q.common_wrong_answer && (
                  <p className="text-xs text-muted-foreground mt-1.5" data-testid={`gap-common-wrong-${q.question_id}`}>
                    Most common wrong answer: <span className="text-rose-700 font-medium">"{q.common_wrong_answer}"</span>
                  </p>
                )}
                {q.missed_by.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1 mt-1.5">
                    <span className="text-[11px] text-muted-foreground mr-0.5">Missed by:</span>
                    {(expanded === q.question_id ? q.missed_by : q.missed_by.slice(0, 5)).map((n, j) => (
                      <Badge key={j} variant="secondary" className="text-[10px] font-normal">{n}</Badge>
                    ))}
                    {q.missed_by.length > 5 && expanded !== q.question_id && (
                      <button className="text-[11px] text-muted-foreground underline"
                              onClick={() => setExpanded(q.question_id)}
                              data-testid={`gap-expand-${q.question_id}`}>
                        +{q.missed_by.length - 5} more
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <p className="text-sm text-muted-foreground py-4 text-center">No question data in submitted attempts.</p>
        )}
      </div>
    </div>
  );
}
